/*
* Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
*/
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import classnames from 'classnames';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {ApplicationFeatures, CRUDPermissions, DownloadJob} from '@ion/core/src/types/entities';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor} from '@ion/dls/src/theme/theme';
import JobDevicesMiniBarChart from './cells/JobDevicesMiniBarChart';
import JobStatusCell from './cells/JobStatusCell';
import {handleDisable} from './JobSearchEngineUtils';
import TaskGridActionMenu from './TaskGridActionMenu';

export type Task = {
  description: string,
  metadata: {
    title?: string
  }
  counts: {
    total: number,
    completed: number,
    pending: number,
    failed: number,
    cancelled: number
  },
  progressStatus: string,
  progress: {
    reason?: string | null,
    status: string
  }
}

export type TaskGridProps = {
  job: DownloadJob | {
    tasks: Task[]
  };
  selectedTask: number;
  setSelectedTask: Function;
  jobsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
  rerunJob?: Function;
  setIsRerunFailed?: Function;
  hasAssignFilePermission?: boolean;
}

export const useStyles = makeStyles(() => {
  return {
    table : {
      maxHeight : '250px'
    },
    headerBorder : {
      borderLeft  : `1px solid ${dlsColor('grey3')}`,
      paddingLeft : 12

    },
    headerCell : {
      color           : dlsColor('grey1'),
      borderTop       : `1px solid ${dlsColor('grey5')}`,
      backgroundColor : dlsColor('white'),
      fontSize        : '12px',
      lineHeight      : '12px',
      letterSpacing   : '1px',
      textTransform   : 'uppercase',
      fontWeight      : 600,
      minHeight       : '40px'
    },
    rowCell : {
      borderTop     : `2px solid ${dlsColor('grey5')}`,
      color         : dlsColor('grey1'),
      fontSize      : '14px',
      lineHeight    : '16px',
      padding       : '4px 12px',
      letterSpacing : '1px',
      maxHeight     : '48px'
    },
    rowCellCount : {
      borderTop     : `2px solid ${dlsColor('grey5')}`,
      color         : dlsColor('grey1'),
      fontSize      : '14px',
      lineHeight    : '16px',
      padding       : '4px 12px 4px 32px',
      letterSpacing : '1px',
      maxHeight     : '48px'
    },
    rowCellUppercase : {
      borderTop     : `2px solid ${dlsColor('grey5')}`,
      color         : dlsColor('grey1'),
      fontSize      : '14px',
      lineHeight    : '16px',
      padding       : '12px',
      textTransform : 'uppercase',
      letterSpacing : '1px'
    },
    tableRow : {
      height          : '48px',
      backgroundColor : dlsColor('white'),
      '&:hover'       : {
        backgroundColor : dlsColor('paleBlue')
      }
    },
    selected : {
      height          : '48px',
      backgroundColor : dlsColor('paleBlue')
    },
    gridText : {
      color      : dlsColor('grey2'),
      fontWeight : 600
    }
  };
});

/**
* The component that displays the job task details of selected job id in table.
*/
export const TasksGrid = ({
  job,
  Features,
  rerunJob,
  selectedTask,
  setSelectedTask,
  jobsPermissions,
  setIsRerunFailed,
  hasAssignFilePermission
}: TaskGridProps) => {
  const classes     = useStyles();
  const tableTitles = translate('jobsearchengine:tableTitles', {returnObjects: true});

  const setClickedEvent = (task, index) => {
    setSelectedTask(index);
  };
  
  return (
    <div>
      <TableContainer id='jobTasksGrid' className={classes.table}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {Object.keys(tableTitles).map((label, index) => (
                <TableCell key={`title-${index}`} align='left'
                  className={classnames(classes.headerCell)}>
                  <div className={index !== 0 ? classes.headerBorder : null}>{tableTitles[label]}</div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {job?.tasks?.map((task: Task, index: number) => {
              const {
                description,
                counts,
                metadata,
                progress,
                progressStatus
              } = task || {};

              const {
                total = 0,
                completed = 0,
                pending = 0,
                failed = 0,
                cancelled = 0
              } = counts || {};
              return (
                <TableRow key={`task-${index}`}
                  classes={{selected: classes.selected}}
                  hover={job?.tasks?.length !== 1 && selectedTask !== index}
                  className={(job?.tasks?.length === 1 || selectedTask === index) ? classes.selected : classes.tableRow}
                  onClick={function(e) {
                    if (e.target.closest('[data-ignore-click]')) {
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    setClickedEvent(task, index);
                  }}>
                  <TableCell className={classnames(classes.rowCellUppercase)}>
                    <div className={classes.gridText}>{translate('jobsearchengine:taskDetails.task')} {index + 1}</div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div className={classes.gridText}>{metadata?.title || description || ''}</div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div>
                      <span className={classes.gridText}>{total}</span>
                    </div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div>
                      <span className={classes.gridText}>{completed}</span>
                    </div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div>
                      <span className={classes.gridText}>{pending}</span>
                    </div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div>
                      <span className={classes.gridText}>{failed}</span>
                    </div>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCell)}>
                    <JobStatusCell status={progressStatus} progress={progress}/>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCell)}>
                    <ConditionalRender condition={!isNil(counts) && total !== cancelled}>
                      <JobDevicesMiniBarChart completed={completed}
                        failed={failed}
                        cancelled={cancelled}
                        inProgress={pending}
                        total={total} />
                    </ConditionalRender>
                  </TableCell>
                  <TableCell className={classnames(classes.rowCellCount)}>
                    <div>
                      <span className={classes.gridText}>{cancelled}</span>
                    </div>
                  </TableCell>
                  <TableCell data-ignore-click className={classnames(classes.rowCell)}>
                    <TaskGridActionMenu job={job}
                      task={task}
                      rerunJob={rerunJob}
                      Features={Features}
                      setIsRerunFailed={setIsRerunFailed}
                      jobsPermissions={jobsPermissions}
                      hasFilePermissionFeature={handleDisable(job, Features, hasAssignFilePermission)} />
                  </TableCell>
                </TableRow>
              );
            })}

          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default TasksGrid;
